import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { buildApiUrl } from '../../config/api';
import axios from 'axios';
import '../../styles/train.css';

const Train = () => {
    const { programId, sessionId } = useParams();
    const { token } = useAuth();
    const navigate = useNavigate();
    const [sessionData, setSessionData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [performances, setPerformances] = useState({});
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [showSaveReminder, setShowSaveReminder] = useState(false);
    const [previousPerformances, setPreviousPerformances] = useState({});
    const [selectedVideo, setSelectedVideo] = useState('');
    const [isModalOpen, setIsModalOpen] = useState(false);

    // Fetch session data
    useEffect(() => {
        const fetchData = async () => {
            try {
                const programResponse = await axios.get(
                    buildApiUrl(`/programs/${programId}`),
                    {
                        headers: { 'Authorization': `Bearer ${token}` }
                    }
                );

                const session = programResponse.data.sessions?.find(
                    s => s.id === parseInt(sessionId)
                );

                if (!session) {
                    throw new Error('Session not found');
                }

                console.log('Raw session data:', session);
                console.log('Exercises data:', session.exercises);
                console.log('First exercise:', session.exercises?.[0]);

                setSessionData(session);
                setLoading(false);

            } catch (err) {
                console.error('Error fetching data:', err);
                setError(err.response?.data?.message || err.message || 'Failed to load session data');
                setLoading(false);
            }
        };

        fetchData();
    }, [programId, sessionId, token]);

    const handleSetLog = (exerciseId, setNumber, field, value) => {
        setPerformances(prev => ({
            ...prev,
            [exerciseId]: {
                ...(prev[exerciseId] || {}),
                [setNumber]: {
                    ...(prev[exerciseId]?.[setNumber] || {}),
                    [field]: value
                }
            }
        }));
        setHasUnsavedChanges(true);
    };

    const handleSavePerformances = async () => {
        setIsSaving(true);
        const currentDate = new Date().toISOString().split('T')[0]; // Format: YYYY-MM-DD

        try {
            // Process each exercise's performances
            const savePromises = sessionData.exercises.map(async (exercise) => {
                const exerciseSets = performances[exercise.id];
                if (!exerciseSets) return; // Skip if no performances logged

                // Convert sets object to array of performances
                const setPromises = Object.entries(exerciseSets).map(async ([setNumber, performance]) => {
                    // Skip empty or incomplete sets
                    if (!performance.weight || !performance.reps) return;

                    const performanceData = {
                        weight: parseFloat(performance.weight),
                        repsPerformed: parseInt(performance.reps),
                        setNumber: parseInt(setNumber),
                        sessionDate: currentDate,
                        notes: "" // Optional: Add notes functionality if needed
                    };

                    // Make the API call for each set
                    return axios.post(
                        buildApiUrl(`/programs/${programId}/sessions/${sessionId}/exercises/${exercise.id}/performances`),
                        performanceData,
                        {
                            headers: { 'Authorization': `Bearer ${token}` }
                        }
                    );
                });

                // Wait for all sets to be saved
                await Promise.all(setPromises.filter(Boolean));
            });

            // Wait for all exercises to be processed
            await Promise.all(savePromises.filter(Boolean));

            setHasUnsavedChanges(false);
            setShowSaveReminder(false);
            alert('Training session saved successfully!');

        } catch (error) {
            console.error('Error saving performances:', error);
            alert(`Failed to save training session: ${error.response?.data?.message || error.message}`);
        } finally {
            setIsSaving(false);
        }
    };

    useEffect(() => {
        const handleBeforeUnload = (e) => {
            if (hasUnsavedChanges) {
                e.preventDefault();
                e.returnValue = '';
                setShowSaveReminder(true);
            }
        };

        window.addEventListener('beforeunload', handleBeforeUnload);
        return () => window.removeEventListener('beforeunload', handleBeforeUnload);
    }, [hasUnsavedChanges]);

    // Add function to fetch previous performances
    const fetchPreviousPerformances = useCallback(async () => {
        if (!sessionData?.exercises) return;

        try {
            const performancePromises = sessionData.exercises.map(async (exercise) => {
                const response = await axios.get(
                    buildApiUrl(`/programs/${programId}/sessions/${sessionId}/exercises/${exercise.id}/performances`),
                    {
                        headers: { 'Authorization': `Bearer ${token}` }
                    }
                );
                
                console.log('Raw API response for exercise:', exercise.id, response.data);
                
                // Group performances by date, maintaining set order within each date
                const performancesByDate = response.data.reduce((acc, perf) => {
                    const date = perf.sessionDate.split('T')[0];
                    if (!acc[date]) {
                        acc[date] = [];
                    }
                    // Ensure we capture the uppercase ID from Golang
                    acc[date].push({
                        id: perf.ID, // Use uppercase ID from Golang
                        weight: perf.weight,
                        repsPerformed: perf.repsPerformed,
                        setNumber: perf.setNumber,
                        sessionDate: perf.sessionDate,
                        exerciseEntryId: perf.exerciseEntryId
                    });
                    return acc;
                }, {});

                // Sort sets within each date
                Object.keys(performancesByDate).forEach(date => {
                    performancesByDate[date].sort((a, b) => a.setNumber - b.setNumber);
                });

                return {
                    exerciseId: exercise.id,
                    performancesByDate
                };
            });

            const results = await Promise.all(performancePromises);
            const performancesMap = results.reduce((acc, { exerciseId, performancesByDate }) => {
                acc[exerciseId] = performancesByDate;
                return acc;
            }, {});

            setPreviousPerformances(performancesMap);
        } catch (error) {
            console.error('Error fetching previous performances:', error);
        }
    }, [programId, sessionId, sessionData, token]);

    // Add to useEffect
    useEffect(() => {
        if (sessionData) {
            fetchPreviousPerformances();
        }
    }, [sessionData, fetchPreviousPerformances]);

    const getYouTubeVideoId = (url) => {
        if (!url) return null;
        const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
        const match = url.match(regExp);
        return (match && match[2].length === 11) ? match[2] : null;
    };

    const handleDemoClick = (youtubeLink) => {
        const videoId = getYouTubeVideoId(youtubeLink);
        if (videoId) {
            setSelectedVideo(videoId);
            setIsModalOpen(true);
        } else {
            console.error('Invalid YouTube URL:', youtubeLink);
            alert('Invalid YouTube video link');
        }
    };

    const handleDeleteDatePerformances = async (exerciseId, date, performances) => {
        console.log('Delete request for:', { exerciseId, date, performances });
        
        const confirmDelete = window.confirm(`Are you sure you want to delete all sets from ${new Date(date).toLocaleDateString()}?`);
        
        if (!confirmDelete) return;

        let hasErrors = false;
        let deletedCount = 0;

        try {
            for (const perf of performances) {
                try {
                    // Log the full performance object to see its structure
                    console.log('Full performance object:', {
                        ...perf,
                        date,
                        exerciseId
                    });

                    // Ensure we have the correct ID
                    const performanceId = perf.ID || perf.id;
                    if (!performanceId) {
                        console.error('Missing performance ID:', perf);
                        hasErrors = true;
                        continue;
                    }

                    const deleteUrl = buildApiUrl(`/programs/${programId}/sessions/${sessionId}/exercises/${exerciseId}/performances/${performanceId}`);
                    console.log('Attempting DELETE request to:', deleteUrl);

                    const response = await axios.delete(
                        deleteUrl,
                        {
                            headers: { 
                                'Authorization': `Bearer ${token}`,
                                'Content-Type': 'application/json'
                            },
                            // Add performance data to request for debugging
                            data: {
                                performanceId,
                                exerciseId,
                                date
                            }
                        }
                    );
                    
                    if (response.status === 204 || response.status === 200) {
                        console.log(`Successfully deleted performance ${performanceId}`);
                        deletedCount++;
                    }
                } catch (error) {
                    // Enhanced error logging with more details
                    console.error('Delete request failed:', {
                        performanceId: perf.ID || perf.id,
                        url: error.config?.url,
                        status: error.response?.status,
                        errorData: error.response?.data,
                        requestData: error.config?.data,
                        fullPerformance: perf
                    });
                    hasErrors = true;
                }
            }

            // Only update state if we actually deleted something
            if (deletedCount > 0) {
                setPreviousPerformances(prev => {
                    const newState = { ...prev };
                    if (newState[exerciseId]) {
                        delete newState[exerciseId][date];
                        if (Object.keys(newState[exerciseId]).length === 0) {
                            delete newState[exerciseId];
                        }
                    }
                    return newState;
                });
            }

            // Refresh data regardless of outcome
            await fetchPreviousPerformances();

            if (hasErrors) {
                alert(`Deleted ${deletedCount} out of ${performances.length} performances. Check console for error details.`);
            } else if (deletedCount > 0) {
                alert(`Successfully deleted ${deletedCount} performances.`);
            }

        } catch (error) {
            console.error('Error in delete operation:', error);
            alert('Error during deletion. Please try again.');
            await fetchPreviousPerformances();
        }
    };

    const handleDeletePerformance = async (exerciseId, performanceId) => {
        console.log(`Attempting to delete single performance: ${performanceId}`);
        const confirmDelete = window.confirm('Are you sure you want to delete this set?');
        
        if (!confirmDelete) return;

        try {
            const response = await axios.delete(
                buildApiUrl(`/programs/${programId}/sessions/${sessionId}/exercises/${exerciseId}/performances/${performanceId}`),
                {
                    headers: { 'Authorization': `Bearer ${token}` }
                }
            );
            
            if (response.status === 204 || response.status === 200) {
                console.log(`Successfully deleted performance ${performanceId}`);
                // Update local state
                setPreviousPerformances(prev => {
                    const newState = { ...prev };
                    // Remove the specific performance from all dates
                    if (newState[exerciseId]) {
                        Object.keys(newState[exerciseId]).forEach(date => {
                            newState[exerciseId][date] = newState[exerciseId][date].filter(
                                perf => perf.id !== performanceId
                            );
                            // Remove date if no performances left
                            if (newState[exerciseId][date].length === 0) {
                                delete newState[exerciseId][date];
                            }
                        });
                        // Remove exercise if no dates left
                        if (Object.keys(newState[exerciseId]).length === 0) {
                            delete newState[exerciseId];
                        }
                    }
                    return newState;
                });
            }
        } catch (error) {
            console.error('Error deleting performance:', error);
            alert('Failed to delete performance. Please refresh and try again.');
        } finally {
            // Refresh data to ensure we're in sync with the backend
            await fetchPreviousPerformances();
        }
    };

    if (loading) return <div className="loading">Loading session data...</div>;
    if (error) return <div className="error">Error: {error}</div>;
    if (!sessionData) return <div className="error">No session found</div>;

    return (
        <div className="training-session">
            <div className="training-header">
                <h2>Training Session {sessionData.name}</h2>
                <div className="header-actions">
                    <button 
                        className={`save-session-btn ${hasUnsavedChanges ? 'unsaved' : ''}`}
                        onClick={handleSavePerformances}
                        disabled={!hasUnsavedChanges || isSaving}
                    >
                        {isSaving ? 'Saving...' : 'Save Session'}
                    </button>
                    <button 
                        onClick={() => navigate(`/programs/${programId}/edit`)}
                        className="back-btn"
                    >
                        Back to Program
                    </button>
                </div>
            </div>

            {/* Save Reminder Popup */}
            {showSaveReminder && hasUnsavedChanges && (
                <div className="save-reminder">
                    <div className="save-reminder-content">
                        <p>Done Training? Save Your Progress!</p>
                        <button 
                            onClick={handleSavePerformances}
                            disabled={isSaving}
                            className="save-reminder-btn"
                        >
                            {isSaving ? 'Saving...' : 'Save Now'}
                        </button>
                    </div>
                </div>
            )}

            <div className="session-table-container">
                <table className="session-table">
                    <colgroup>
                        <col className="series-col" />
                        <col className="name-col" />
                        <col className="demo-col" />
                        <col className="sets-col" />
                        <col className="reps-col" />
                        <col className="tempo-col" />
                        <col className="rest-col" />
                        <col className="notes-col" />
                    </colgroup>
                    <thead>
                        <tr>
                            <th>Series</th>
                            <th>Exercise Name</th>
                            <th>Demo</th>
                            <th>Sets</th>
                            <th>Reps</th>
                            <th>Tempo</th>
                            <th>Rest</th>
                            <th>Notes</th>
                        </tr>
                    </thead>
                    <tbody>
                        {sessionData.exercises.map((exercise) => (
                            <React.Fragment key={exercise.id}>
                                <tr className="exercise-params-row">
                                    <td>{exercise.series}</td>
                                    <td>{exercise.exerciseName}</td>
                                    <td>
                                        <button 
                                            className="demo-button"
                                            onClick={() => {
                                                console.log('Full exercise object:', exercise);
                                                console.log('YouTube link value:', exercise.youTubeLink);
                                                console.log('All available properties:', Object.keys(exercise));
                                                handleDemoClick(exercise.youTubeLink);
                                            }}
                                        >
                                            Demo
                                        </button>
                                    </td>
                                    <td>{exercise.sets}</td>
                                    <td>{exercise.reps}</td>
                                    <td>{exercise.tempo}</td>
                                    <td>{exercise.rest}</td>
                                    <td>{exercise.notes}</td>
                                </tr>
                                {/* Performance Logging Row */}
                                <tr className="performance-row">
                                    <td colSpan="8">
                                        <div className="performance-container">
                                            <div className="sets-container">
                                                {Array.from({ length: 10 }).map((_, index) => (
                                                    <div key={index} className="set-input-group">
                                                        <label>Set {index + 1}</label>
                                                        <input
                                                            type="number"
                                                            placeholder="kg"
                                                            value={performances[exercise.id]?.[index + 1]?.weight || ''}
                                                            onChange={(e) => handleSetLog(exercise.id, index + 1, 'weight', e.target.value)}
                                                            className="weight-input"
                                                        />
                                                        <input
                                                            type="number"
                                                            placeholder="reps"
                                                            value={performances[exercise.id]?.[index + 1]?.reps || ''}
                                                            onChange={(e) => handleSetLog(exercise.id, index + 1, 'reps', e.target.value)}
                                                            className="reps-input"
                                                        />
                                                    </div>
                                                ))}
                                            </div>
                                            {previousPerformances[exercise.id] && Object.entries(previousPerformances[exercise.id]).map(([date, sets]) => (
                                                <div key={date} className="previous-performance">
                                                    <div className="previous-performance-header">
                                                        <h4>{new Date(date).toLocaleDateString()}</h4>
                                                        <button 
                                                            className="delete-date-btn"
                                                            onClick={() => handleDeleteDatePerformances(exercise.id, date, sets)}
                                                            title="Delete all sets from this date"
                                                        >
                                                            ×
                                                        </button>
                                                    </div>
                                                    {sets.map((perf) => (
                                                        <div key={perf.id} className="previous-set">
                                                            <span className="set-number">Set {perf.setNumber}</span>
                                                            <span className="weight">{perf.weight}kg</span>
                                                            <span className="reps">×{perf.repsPerformed}</span>
                                                            <button 
                                                                className="delete-set-btn"
                                                                onClick={() => handleDeletePerformance(exercise.id, perf.id)}
                                                                title="Delete this set"
                                                            >
                                                                ×
                                                            </button>
                                                        </div>
                                                    ))}
                                                </div>
                                            ))}
                                        </div>
                                    </td>
                                </tr>
                            </React.Fragment>
                        ))}
                    </tbody>
                </table>
            </div>

            {/* Video Modal */}
            {isModalOpen && (
                <div className="video-modal-overlay" onClick={() => setIsModalOpen(false)}>
                    <div className="video-modal-content" onClick={e => e.stopPropagation()}>
                        <div className="modal-header">
                            <button className="close-modal" onClick={() => setIsModalOpen(false)}>×</button>
                        </div>
                        <div className="video-container">
                            <iframe
                                width="560"
                                height="315"
                                src={`https://www.youtube.com/embed/${selectedVideo}`}
                                title="Exercise Demo"
                                frameBorder="0"
                                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                allowFullScreen
                            ></iframe>
                        </div>
                        <div className="modal-footer">
                            <button 
                                className="back-to-training-btn"
                                onClick={() => setIsModalOpen(false)}
                            >
                                Back to Training
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Train; 